import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const CODE_COLORS = {
  NL: "#FF6B6B",
  EN: "#F9D876",
  RO: "#4ECDC4",
  DE: "#45B7D1",
  FR: "#9B5DE5",
};

const SmallCard = ({ item, index }) => (
  <motion.article
    whileHover={{ y: -6 }}
    transition={{ type: "spring", stiffness: 300, damping: 24 }}
    className="group relative h-full overflow-hidden rounded-[1.75rem] border border-black/[0.06] bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
    data-testid={`language-card-${item.code.toLowerCase()}`}
  >
    <div className="flex items-start justify-between gap-4">
      <h3 className="font-display text-3xl font-semibold tracking-tight text-[#1A1A1A]">
        {item.title}
        {item.sub && <span className="mt-1 block font-sans text-sm font-normal text-[#5C5C5C]">{item.sub}</span>}
      </h3>
      <span
        className="font-display text-3xl font-semibold italic transition-transform duration-300 group-hover:scale-110"
        style={{ color: CODE_COLORS[item.code] }}
      >
        {item.code}
      </span>
    </div>
    <p className="mt-4 text-sm leading-relaxed text-[#5C5C5C] md:text-base">{item.desc}</p>
    <span
      className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-[#FF6B6B] via-[#4ECDC4] to-[#45B7D1] transition-all duration-500 group-hover:w-full"
      aria-hidden="true"
    />
  </motion.article>
);

const Languages = () => {
  const { t } = useLanguage();
  const { dutch, others } = t.languages;

  return (
    <section id="limbi" data-testid="section-languages" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#5C5C5C]">{t.languages.eyebrow}</p>
              <h2 className="mt-5 max-w-xl font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#1A1A1A] md:text-5xl">
                {t.languages.title}
              </h2>
            </div>
            <span className="hidden h-px flex-1 bg-gradient-to-r from-transparent via-black/15 to-transparent md:mx-10 md:block" aria-hidden="true" />
            <p className="max-w-xs text-sm leading-relaxed text-[#5C5C5C]">{t.languages.footnote}</p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-8 md:row-span-2" delay={0.05}>
            <motion.article
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="relative flex h-full flex-col justify-between overflow-hidden rounded-[2rem] bg-[#1A1A1A] p-9 text-[#FDFBF7] md:p-12"
              data-testid="language-card-nl"
            >
              <div className="pointer-events-none absolute -right-16 -top-24 font-display text-[16rem] font-semibold italic leading-none text-white/[0.05]" aria-hidden="true">
                NL
              </div>
              <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-gradient-to-tr from-[#FF6B6B]/40 via-[#F9D876]/30 to-[#45B7D1]/40 blur-3xl" aria-hidden="true" />

              <div className="relative">
                <div className="flex items-center gap-4">
                  <span className="font-display text-4xl font-semibold italic text-[#F9D876]">{dutch.code}</span>
                  <span className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-white/80">
                    {dutch.tag}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-4xl font-medium tracking-tight md:text-5xl">{dutch.title}</h3>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">{dutch.desc}</p>
              </div>

              <ul className="relative mt-10 grid gap-3 sm:grid-cols-2">
                {dutch.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-white/85">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#F9D876]">
                      <Check className="h-3 w-3 text-[#1A1A1A]" strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#FF6B6B] via-[#F9D876] via-40% to-[#45B7D1]" aria-hidden="true" />
            </motion.article>
          </Reveal>

          {others.map((item, i) => (
            <Reveal key={item.code} delay={0.1 + i * 0.06} className={i < 2 ? "md:col-span-4" : "md:col-span-6"}>
              <SmallCard item={item} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Languages;
